import { createWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';

import { TOOL_ID, FOLDER_NAME } from '@/consts/configs';

import {
  createNavigationSlice,
  navigationInitialState,
  createLibrarySlice,
  libraryInitialState,
  createViewSlice,
  createPresetSlice,
  presetInitialState,
  createInputsSlice,
  inputsInitialState,
  createGenerationSlice,
  generationInitialState,
  createResultSlice,
  resultInitialState,
  createFilesSlice,
  filesInitialState,
  createUserSlice,
  createProjectSlice,
  projectInitialState,
  createCanvasSlice,
  createToastSlice,
  type NavigationSlice,
  type LibrarySlice,
  type ViewSlice,
  type PresetSlice,
  type InputsSlice,
  type GenerationSlice,
  type ResultSlice,
  type FilesSlice,
  type UserSlice,
  type ProjectSlice,
  type CanvasSlice,
  type ToastSlice,
} from './slices';

// a === b for primitive data types, shallow for complex data types
const compare = <T>(a: T, b: T) => a === b || shallow(a, b);

/**
 * Drive constants slice - static values, no setters needed
 */
interface DriveSlice {
  toolId: string;
  folderName: string;
}

/**
 * Reset actions slice - operates across multiple slices
 */
interface ResetSlice {
  resetCreate: () => void;
  resetAll: () => void;
}

/**
 * Combined app state from all slices
 */
export type AppState = NavigationSlice &
  LibrarySlice &
  ViewSlice &
  PresetSlice &
  InputsSlice &
  GenerationSlice &
  ResultSlice &
  FilesSlice &
  UserSlice &
  ProjectSlice &
  CanvasSlice &
  ToastSlice &
  DriveSlice &
  ResetSlice;

export const useAppStore = createWithEqualityFn<AppState>()(
  (...args) => ({
    // Compose all slices
    ...createNavigationSlice(...args),
    ...createLibrarySlice(...args),
    ...createViewSlice(...args),
    ...createPresetSlice(...args),
    ...createInputsSlice(...args),
    ...createGenerationSlice(...args),
    ...createResultSlice(...args),
    ...createFilesSlice(...args),
    ...createUserSlice(...args),
    ...createProjectSlice(...args),
    ...createCanvasSlice(...args),
    ...createToastSlice(...args),

    // Drive constants - static values
    toolId: TOOL_ID,
    folderName: FOLDER_NAME,

    // Reset actions - operate across multiple slices
    resetCreate: () => {
      const [set] = args;
      set({
        ...presetInitialState,
        ...inputsInitialState,
        ...resultInitialState,
        ...generationInitialState,
        projectCreditsCount: projectInitialState.projectCreditsCount,
        hasAutoSelectedPreset: libraryInitialState.hasAutoSelectedPreset,
      });
    },

    resetAll: () => {
      const [set] = args;
      set({
        screen: navigationInitialState.screen,
        ...presetInitialState,
        ...inputsInitialState,
        ...resultInitialState,
        ...generationInitialState,
        ...filesInitialState,
        ...projectInitialState,
        hasAutoSelectedPreset: libraryInitialState.hasAutoSelectedPreset,
        shouldScrollToActiveTab: libraryInitialState.shouldScrollToActiveTab,
      });
    },
  }),
  compare
);

export const getAppStore = useAppStore.getState;
export const setAppStore = useAppStore.setState;

export type { AppState as IAppStore };
